import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, useReactFlow, useStore } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Button, Input, message, Typography, Switch, Slider, Collapse, Tooltip } from 'antd';
import { PictureOutlined, FontSizeOutlined, PlayCircleFilled, LoadingOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE, NODE_BG } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useGeneration } from '../../../context/GenerationContext';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const MoondreamQueryNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Local state for prompt input (used when no text node is connected)
    const [promptInput, setPromptInput] = useState(data.prompt || '');

    // Settings state
    const [reasoning, setReasoning] = useState(data.reasoning !== false); // default true
    const [temperature, setTemperature] = useState(data.temperature || 0);
    const [topP, setTopP] = useState(data.topP || 1);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Optimized selector to get connected image from left-bottom handle
    const connectedImageSrc = useStore(
        useCallback((s) => {
            const edge = s.edges.find((e) => e.target === id && e.targetHandle === 'left-bottom');
            if (!edge) return null;
            return s.nodeInternals.get(edge.source)?.data?.src;
        }, [id])
    );

    // Optimized selector to get connected text from left-top handle
    const connectedText = useStore(
        useCallback((s) => {
            const edge = s.edges.find((e) => e.target === id && e.targetHandle === 'left-top');
            if (!edge) return null;
            return s.nodeInternals.get(edge.source)?.data?.text;
        }, [id])
    );

    const updateData = useCallback((key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    }, [id, setNodes, data]);

    // Batch update to avoid race conditions
    const updateDataBatch = useCallback((updates) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        ...updates
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, updates);
        }
    }, [id, setNodes, data]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            // Get image URL (connected or stored)
            const imageUrl = connectedImageSrc || data.inputImage;
            if (!imageUrl) {
                message.warning('Please connect an image to the bottom-left handle.');
                setGenerating(false);
                return;
            }

            // Get prompt (connected text takes precedence, then local input)
            const prompt = connectedText || promptInput;
            if (!prompt) {
                message.warning('Please enter a query or connect a text node to the top-left handle.');
                setGenerating(false);
                return;
            }

            // Build payload
            const payload = {
                image_url: imageUrl,
                prompt: prompt,
                reasoning: reasoning
            };

            // Add optional params if non-default
            if (temperature > 0) {
                payload.temperature = temperature;
            }
            if (topP < 1) {
                payload.top_p = topP;
            }

            // Call API
            const response = await api.post('/fal/moondream/query', payload);

            const { request_id } = response.data;
            const modelId = 'fal-ai/moondream3-preview/query';

            trackGeneration(request_id, modelId, {
                prompt: prompt,
                type: 'Text',
                canvas_id: canvasId,
                node_id: id,
                promptProp: `Query: ${prompt.slice(0, 50)}...`
            }, {
                onComplete: async (result) => {
                    if (result && result.output) {
                        updateDataBatch({
                            output: result.output,
                            reasoning_text: result.reasoning || null,
                            text: result.output
                        });
                        message.success('Query complete!');
                    } else {
                        message.error('Failed to get response from Moondream.');
                        console.error('Unexpected result:', result);
                    }
                    setGenerating(false);
                },
                onFail: (err) => {
                    console.error("Moondream query failed via queue:", err);
                    message.error('Query failed: ' + (err.message || 'Unknown error'));
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Moondream query error:', error);
            message.error('Query failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false);
        }
    };

    const handlePromptChange = (e) => {
        const value = e.target.value;
        setPromptInput(value);
        updateData('prompt', value);
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={350}
                minHeight={400}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Moondream Query'}
                iconType="ant-design"
                icon={<EyeOutlined />}
                color="#722ed1"
                onTitleChange={onTitleChange}
            />

            {/* Input Handles */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-top"
                style={{
                    ...getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible),
                    top: 40
                }}
            />

            <Handle
                type="target"
                position={Position.Left}
                id="left-bottom"
                style={{
                    ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible),
                    bottom: 40,
                    top: 'auto'
                }}
            />

            {/* Output Handle - Text */}
            <Handle
                type="source"
                position={Position.Right}
                id="right-output"
                style={getHandleStyle(TYPE_COLORS.text, 'right', handlesVisible)}
            />

            {/* Generate Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 64 }} /> : <PlayCircleFilled style={{ fontSize: 64 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -160,
                    bottom: 0,
                    width: 130,
                    height: 130,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '10px solid #161b22',
                    background: '#722ed1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 12, height: '100%', display: 'flex', flexDirection: 'column', background: NODE_BG }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #722ed1' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #722ed1, 0 0 30px rgba(114, 46, 209, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 8,
                    overflow: 'hidden',
                    animation: generating ? 'pulse-glow-purple 2s infinite' : 'none'
                }}
            >
                {/* Prompt Input Area */}
                <div style={{ marginBottom: 12 }}>
                    <Text style={{ color: '#8b949e', fontSize: 11, marginBottom: 4, display: 'block' }}>
                        {connectedText ? 'Prompt (from connected node):' : 'Query Prompt:'}
                    </Text>
                    <TextArea
                        className="nodrag"
                        value={connectedText || promptInput}
                        onChange={handlePromptChange}
                        disabled={!!connectedText}
                        placeholder="Ask something about the image..."
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{
                            background: '#0d1117',
                            borderColor: '#30363d',
                            color: '#c9d1d9',
                            resize: 'none'
                        }}
                    />
                </div>

                {/* Settings */}
                <Collapse
                    ghost
                    size="small"
                    className="nodrag"
                    style={{ marginBottom: 12 }}
                >
                    <Panel
                        header={<Text style={{ color: '#8b949e', fontSize: 11 }}><SettingOutlined /> Settings</Text>}
                        key="settings"
                        style={{ padding: 0 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, flex: 1 }}>Include Reasoning</Text>
                            <Switch
                                size="small"
                                checked={reasoning}
                                onChange={(v) => {
                                    setReasoning(v);
                                    updateData('reasoning', v);
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Temperature: {temperature}</Text>
                            <Slider
                                min={0}
                                max={1}
                                step={0.1}
                                value={temperature}
                                onChange={(v) => {
                                    setTemperature(v);
                                    updateData('temperature', v);
                                }}
                            />
                        </div>
                        <div>
                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Top P: {topP}</Text>
                            <Slider
                                min={0}
                                max={1}
                                step={0.1}
                                value={topP}
                                onChange={(v) => {
                                    setTopP(v);
                                    updateData('topP', v);
                                }}
                            />
                        </div>
                    </Panel>
                </Collapse>

                {/* Output Area */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.output ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <Text style={{ color: '#8b949e', fontSize: 11, marginBottom: 4, display: 'block' }}>Answer:</Text>
                                <div style={{
                                    background: '#0d1117',
                                    border: '1px solid #30363d',
                                    borderRadius: 6,
                                    padding: 12,
                                    maxHeight: 150,
                                    overflow: 'auto'
                                }}>
                                    <Paragraph
                                        copyable
                                        style={{ color: '#c9d1d9', margin: 0, whiteSpace: 'pre-wrap' }}
                                    >
                                        {data.output}
                                    </Paragraph>
                                </div>
                            </div>
                            {data.reasoning_text && (
                                <div>
                                    <Text style={{ color: '#8b949e', fontSize: 11, marginBottom: 4, display: 'block' }}>Reasoning:</Text>
                                    <div style={{
                                        background: '#0d1117',
                                        border: '1px solid #21262d',
                                        borderRadius: 6,
                                        padding: 12,
                                        maxHeight: 100,
                                        overflow: 'auto'
                                    }}>
                                        <Paragraph
                                            style={{ color: '#8b949e', margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}
                                        >
                                            {data.reasoning_text}
                                        </Paragraph>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#8b949e',
                            padding: 20,
                            textAlign: 'center'
                        }}>
                            <EyeOutlined style={{ fontSize: 48, marginBottom: 16, color: '#722ed1' }} />
                            <Text style={{ color: '#8b949e', fontSize: 14 }}>Connect Image & Enter Query</Text>
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                                Top Left: Query Text (optional)<br />
                                Bottom Left: Image Source
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <style>{`
                @keyframes pulse-glow-purple {
                    0% {
                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(114, 46, 209, 0.4);
                        border-color: #722ed1;
                    }
                    50% {
                        box-shadow: 0 0 25px #9254de, 0 0 50px rgba(114, 46, 209, 0.6);
                        border-color: #9254de;
                    }
                    100% {
                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(114, 46, 209, 0.4);
                        border-color: #722ed1;
                    }
                }
            `}</style>
        </div>
    );
};

export default memo(MoondreamQueryNode);
